import { useMutateGetFile } from "@/api/s3.api";
import { useState } from "react";

export interface DownloadZipResult {
  total: number;
  succeeded: number;
  failed: number;
}

export interface DownloadZipOptions {
  /**
   * Filename used for the generated archive (without `.zip` extension).
   * Defaults to `archive_<timestamp>`.
   */
  zipFilename?: string;
  /**
   * Optional override mapping a S3 key to the filename inside the archive.
   * If omitted, the basename of the key is used.
   */
  getEntryName?: (key: string, index: number) => string;
}

/**
 * Generic hook to download a list of S3 keys, bundle them into a zip
 * and trigger a browser download.
 *
 * - Lazy-loads `jszip` so it is not in the main bundle.
 * - Handles filename collisions by prefixing duplicates with their index.
 * - Returns a summary so the caller can decide how to surface the result
 *   (toast, modal, console, ...).
 */
export const useDownloadZip = () => {
  const { mutateAsync: getFile } = useMutateGetFile();
  const [isDownloading, setIsDownloading] = useState(false);

  const download = async (
    keys: string[],
    options: DownloadZipOptions = {},
  ): Promise<DownloadZipResult> => {
    const result: DownloadZipResult = {
      total: keys.length,
      succeeded: 0,
      failed: 0,
    };

    if (keys.length === 0) return result;

    setIsDownloading(true);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const used = new Set<string>();

      await Promise.all(
        keys.map(async (key, index) => {
          try {
            const url = await getFile(key);
            const blob = await (await fetch(url)).blob();
            const baseName =
              options.getEntryName?.(key, index) ?? key.split("/").pop() ?? `file_${index + 1}`;
            const name = used.has(baseName) ? `${index + 1}_${baseName}` : baseName;
            used.add(name);
            zip.file(name, blob);
            result.succeeded += 1;
          } catch (e) {
            result.failed += 1;
            console.error("Failed to download file for key:", key, e);
          }
        }),
      );

      if (result.succeeded === 0) return result;

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const objectUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${options.zipFilename ?? `archive_${Date.now()}`}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);

      return result;
    } finally {
      setIsDownloading(false);
    }
  };

  return { download, isDownloading };
};
